/**
 * Data Access Layer for Completion Reminders
 * Supports reminders for profile and happening completion
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import type { CompletionReminder, CompletionReminderCreate, CompletionReminderUpdate } from './types.js';

/**
 * Options for the bulk delete helpers.
 *
 * `keepImmediate` spares offset_hours = 0 rows. Those are one-off sends someone
 * triggered on purpose (an invite, a "send now"), not part of the schedule the edit
 * dialog rewrites on save. Deleting and recreating them on save resets sent_at, and
 * the scheduler sends any unsent immediate row — so every save would email again.
 */
export interface DeleteRemindersOptions {
  keepImmediate?: boolean;
  /**
   * Spare rows that already went out. A sent row is the record that stops a resend:
   * a timed reminder stays inside the scheduler's send window for an hour after it
   * fires, so a save in that hour that recreated it unsent emailed the artist twice.
   */
  keepSent?: boolean;
  /** Only rows with no happening_id (person-level profile reminders from the People page). */
  unlinkedOnly?: boolean;
}

function applyDeleteOptions<Q extends { neq: (c: string, v: unknown) => Q; is: (c: string, v: null) => Q }>(
  query: Q,
  options: DeleteRemindersOptions
): Q {
  let q = query;
  if (options.keepImmediate) q = q.neq('offset_hours', 0);
  if (options.keepSent) q = q.is('sent_at', null);
  if (options.unlinkedOnly) q = q.is('happening_id', null);
  return q;
}

/**
 * Get all completion reminders for an entity
 */
export async function getCompletionReminders(
  reminderType: 'profile' | 'happening',
  entityId: string
): Promise<CompletionReminder[]> {
  const { data, error } = await getClient()
    .from(TABLES.completion_reminders)
    .select('*')
    .eq('reminder_type', reminderType)
    .eq('entity_id', entityId)
    .order('offset_hours', { ascending: true });

  if (error) throw error;
  return (data || []) as CompletionReminder[];
}

/**
 * Create a completion reminder
 */
export async function createCompletionReminder(input: CompletionReminderCreate): Promise<CompletionReminder> {
  const { data, error } = await getClient()
    .from(TABLES.completion_reminders)
    .insert(input)
    .select()
    .single();

  if (error) throw error;
  return data as CompletionReminder;
}

/**
 * Update a completion reminder
 */
export async function updateCompletionReminder(
  id: string,
  input: CompletionReminderUpdate
): Promise<CompletionReminder> {
  const { data, error } = await getClient()
    .from(TABLES.completion_reminders)
    .update(input)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as CompletionReminder;
}

/**
 * Delete a completion reminder
 */
export async function deleteCompletionReminder(id: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.completion_reminders)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * Delete all completion reminders for an entity
 */
export async function deleteCompletionReminders(
  reminderType: 'profile' | 'happening',
  entityId: string,
  options: DeleteRemindersOptions = {}
): Promise<void> {
  let query = getClient()
    .from(TABLES.completion_reminders)
    .delete()
    .eq('reminder_type', reminderType)
    .eq('entity_id', entityId);
  query = applyDeleteOptions(query, options);

  const { error } = await query;
  if (error) throw error;
}

/**
 * Mark a reminder as sent
 */
export async function markCompletionReminderSent(reminderId: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.completion_reminders)
    .update({ sent_at: new Date().toISOString() })
    .eq('id', reminderId);

  if (error) throw error;
}

/**
 * Park a reminder that cannot be delivered however many times it is retried —
 * an unusable recipient address, for instance. The scheduler skips reminders
 * carrying an error_message, so this converts an every-15-minutes failure into a
 * single recorded one the admin UI can show.
 *
 * Only for permanent failures: a transient error (mail provider down) must stay
 * unparked so the next run retries it.
 */
export async function markCompletionReminderFailed(
  reminderId: string,
  errorMessage: string
): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.completion_reminders)
    .update({ error_message: errorMessage })
    .eq('id', reminderId);

  if (error) throw error;
}

/**
 * Queue a reminder to go out on the scheduler's next run ("send now").
 *
 * The unique index on (reminder_type, entity_id, offset_hours, happening_id) allows
 * one immediate row per target, so a second "send now" reuses that row: clearing
 * sent_at and error_message puts it back in the queue, and bumping created_at keeps
 * the scheduler's "immediate rows are due from created_at" rule true.
 */
export async function queueImmediateCompletionReminder(
  input: Omit<CompletionReminderCreate, 'offset_hours'>
): Promise<CompletionReminder> {
  const client = getClient();
  let lookup = client
    .from(TABLES.completion_reminders)
    .select('id')
    .eq('reminder_type', input.reminder_type)
    .eq('entity_id', input.entity_id)
    .eq('offset_hours', 0);
  lookup = input.happening_id ? lookup.eq('happening_id', input.happening_id) : lookup.is('happening_id', null);

  const { data: existing, error: lookupError } = await lookup.limit(1);
  if (lookupError) throw lookupError;

  if (existing && existing.length > 0) {
    const { data, error } = await client
      .from(TABLES.completion_reminders)
      .update({
        recipient_email: input.recipient_email,
        recipient_name: input.recipient_name,
        sent_at: null,
        error_message: null,
        created_at: new Date().toISOString(),
      })
      .eq('id', existing[0].id)
      .select()
      .single();
    if (error) throw error;
    return data as CompletionReminder;
  }

  return createCompletionReminder({ ...input, offset_hours: 0 });
}

/**
 * Whether a string is shaped like an email address: something, an @, a domain
 * with a dot. Deliberately shallow — it is here to catch placeholders like
 * 'soon@soon' that the mail provider rejects outright, not to judge
 * deliverability.
 */
export function isLikelyEmailAddress(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

/**
 * Get profile reminders linked to a specific happening
 */
export async function getProfileRemindersForHappening(happeningId: string): Promise<CompletionReminder[]> {
  const { data, error } = await getClient()
    .from(TABLES.completion_reminders)
    .select('*')
    .eq('reminder_type', 'profile')
    .eq('happening_id', happeningId)
    .order('offset_hours', { ascending: true });

  if (error) throw error;
  return (data || []) as CompletionReminder[];
}

/**
 * Delete profile reminders linked to a specific happening
 */
export async function deleteProfileRemindersForHappening(
  happeningId: string,
  options: DeleteRemindersOptions = {}
): Promise<void> {
  let query = getClient()
    .from(TABLES.completion_reminders)
    .delete()
    .eq('reminder_type', 'profile')
    .eq('happening_id', happeningId);
  query = applyDeleteOptions(query, options);

  const { error } = await query;
  if (error) throw error;
}

/**
 * Get combined artist/show reminders linked to a specific happening
 */
export async function getCombinedRemindersForHappening(happeningId: string): Promise<CompletionReminder[]> {
  const { data, error } = await getClient()
    .from(TABLES.completion_reminders)
    .select('*')
    .eq('reminder_type', 'combined_artist_show')
    .eq('happening_id', happeningId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data || []) as CompletionReminder[];
}

/**
 * Delete combined artist/show reminders linked to a specific happening
 */
export async function deleteCombinedRemindersForHappening(happeningId: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.completion_reminders)
    .delete()
    .eq('reminder_type', 'combined_artist_show')
    .eq('happening_id', happeningId);

  if (error) throw error;
}

/**
 * Get post-show reminders linked to a specific happening
 */
export async function getPostShowRemindersForHappening(happeningId: string): Promise<CompletionReminder[]> {
  const { data, error } = await getClient()
    .from(TABLES.completion_reminders)
    .select('*')
    .eq('reminder_type', 'post_show')
    .eq('happening_id', happeningId)
    .order('offset_hours', { ascending: true });

  if (error) throw error;
  return (data || []) as CompletionReminder[];
}

/**
 * Delete post-show reminders linked to a specific happening
 */
export async function deletePostShowRemindersForHappening(
  happeningId: string,
  options: DeleteRemindersOptions = {}
): Promise<void> {
  let query = getClient()
    .from(TABLES.completion_reminders)
    .delete()
    .eq('reminder_type', 'post_show')
    .eq('happening_id', happeningId);
  query = applyDeleteOptions(query, options);

  const { error } = await query;
  if (error) throw error;
}



